import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

// Context for managing centralized loading state
const LoadingContext = createContext(null);

// Minimum time to show loader (ms) - ensures animation is visible even for cached data
const MIN_LOADER_DURATION = 800;

export function LoadingProvider({ children }) {
    const [loadingSources, setLoadingSources] = useState(new Set());
    const [success, setSuccess] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const wasLoading = useRef(false);
    const loaderStartTime = useRef(null);
    const minDurationTimeout = useRef(null);
    const safetyTimeout = useRef(null);

    // Register a loading source
    const registerLoading = useCallback((sourceId) => {
        console.log('[LoadingContext] registerLoading:', sourceId);
        setLoadingSources(prev => {
            const next = new Set(prev);
            next.add(sourceId);
            return next;
        });
        setShowLoader(true);
        setSuccess(false);
        setIsAnimating(true);
        if (!loaderStartTime.current) {
            loaderStartTime.current = Date.now();
        }
    }, []);

    // Unregister a loading source (loading complete)
    const unregisterLoading = useCallback((sourceId) => {
        console.log('[LoadingContext] unregisterLoading:', sourceId);
        setLoadingSources(prev => {
            const next = new Set(prev);
            next.delete(sourceId);
            return next;
        });
    }, []);

    const isLoading = loadingSources.size > 0;

    // Track transition from loading to not-loading
    useEffect(() => {
        console.log('[LoadingContext] isLoading changed:', isLoading, 'wasLoading:', wasLoading.current);
        if (wasLoading.current && !isLoading) {
            const elapsed = Date.now() - (loaderStartTime.current || Date.now());
            const remaining = Math.max(0, MIN_LOADER_DURATION - elapsed);
            console.log('[LoadingContext] Loading complete. Elapsed:', elapsed, 'Remaining:', remaining);

            minDurationTimeout.current = setTimeout(() => {
                console.log('[LoadingContext] Setting success=true');
                setSuccess(true);
            }, remaining);
        }
        wasLoading.current = isLoading;

        return () => {
            if (minDurationTimeout.current) {
                clearTimeout(minDurationTimeout.current);
            }
        };
    }, [isLoading]);

    // Called when loader animation completes
    const onAnimationComplete = useCallback(() => {
        console.log('[LoadingContext] onAnimationComplete called');
        setShowLoader(false);
        setSuccess(false);
        setIsAnimating(false);
        loaderStartTime.current = null;
        if (safetyTimeout.current) {
            clearTimeout(safetyTimeout.current);
            safetyTimeout.current = null;
        }
    }, []);

    // Safety net: if success is true but onAnimationComplete never fires,
    // force-complete after 5 seconds to prevent stuck loading screen
    useEffect(() => {
        if (success && isAnimating) {
            safetyTimeout.current = setTimeout(() => {
                console.warn('[LoadingContext] Safety timeout: forcing animation complete');
                setShowLoader(false);
                setSuccess(false);
                setIsAnimating(false);
                loaderStartTime.current = null;
            }, 5000);
        }
        return () => {
            if (safetyTimeout.current) {
                clearTimeout(safetyTimeout.current);
                safetyTimeout.current = null;
            }
        };
    }, [success, isAnimating]);

    const shouldShowContent = !isLoading && !isAnimating;

    useEffect(() => {
        console.log('[LoadingContext] State:', { showLoader, success, isAnimating, shouldShowContent, sources: Array.from(loadingSources) });
    }, [showLoader, success, isAnimating, shouldShowContent, loadingSources]);

    const value = {
        isLoading,
        success,
        showLoader,
        isAnimating,
        shouldShowContent,
        registerLoading,
        unregisterLoading,
        onAnimationComplete,
    };

    return (
        <LoadingContext.Provider value={value}>
            {children}
        </LoadingContext.Provider>
    );
}

export function useLoading() {
    const context = useContext(LoadingContext);
    if (!context) {
        // Return safe defaults when outside provider (e.g., during error boundary or unmounting)
        return {
            isLoading: false,
            success: false,
            showLoader: false,
            isAnimating: false,
            shouldShowContent: true,
            registerLoading: () => { },
            unregisterLoading: () => { },
            onAnimationComplete: () => { },
        };
    }
    return context;
}

/**
 * Track if this is a fresh session (page reload) or client-side navigation.
 * On fresh session, we always show the loader. On navigation, skip if data is cached.
 */
let isFirstSessionLoad = true;

/**
 * Hook that shows loader on mount, with smart caching detection.
 * - Always shows loader on page reload (fresh session)
 * - Skips loader on navigation if data is already cached
 */
export function usePageLoading(sourceId, isLoadingState) {
    const { registerLoading, unregisterLoading, shouldShowContent } = useLoading();
    const hasMounted = useRef(false);
    const unregisterTimer = useRef(null);
    // On first session load (reload), always show loader. On navigation, check cache.
    const shouldSkipLoader = useRef(!isFirstSessionLoad && !isLoadingState);

    // Only register loading if we shouldn't skip (not a cached navigation)
    useEffect(() => {
        if (!shouldSkipLoader.current) {
            console.log('[usePageLoading mount] registering:', sourceId);
            registerLoading(sourceId);
        } else {
            console.log('[usePageLoading mount] skipping (cached navigation):', sourceId);
        }
        hasMounted.current = true;

        // After first component mounts, mark session as not first load anymore
        isFirstSessionLoad = false;

        return () => {
            console.log('[usePageLoading unmount]', sourceId);
            if (unregisterTimer.current) {
                clearTimeout(unregisterTimer.current);
            }
            unregisterLoading(sourceId);
        };
    }, [sourceId, registerLoading, unregisterLoading]);

    // When loading state becomes false, DELAY unregister so wasLoading can be set
    useEffect(() => {
        if (!hasMounted.current) return;

        if (!isLoadingState) {
            // Delay unregister to next tick so React can batch the register first
            unregisterTimer.current = setTimeout(() => {
                console.log('[usePageLoading] Data ready, unregistering:', sourceId);
                unregisterLoading(sourceId);
            }, 50);
        }

        return () => {
            if (unregisterTimer.current) {
                clearTimeout(unregisterTimer.current);
            }
        };
    }, [sourceId, isLoadingState, unregisterLoading]);

    console.log('[usePageLoading]', sourceId, 'shouldShowContent:', shouldShowContent);
    return shouldShowContent;
}

export { LoadingContext };
